import { useId } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Section } from '@/shared/ui/layout/Section';
import { cn } from '@/shared/utils/cn';
import type { AdminParcelCreateFormValues } from '@/features/parcel-form/modules/types';

// Reason ``Omborga qoʻshish`` is disabled. ``null`` -> enabled.
// ``no-trek-id`` — operator hasn't typed a Trek ID yet.
// ``already-in-warehouse`` — backend already has an UNCLAIMED OwnerlessProduct
//   for this trek_id, so ticking would be a no-op (the parcel will auto-link).
export type AddToWarehouseDisabledReason =
  | 'no-trek-id'
  | 'already-in-warehouse'
  | null;

type Props = {
  form: UseFormReturn<AdminParcelCreateFormValues>;
  disabledReason: AddToWarehouseDisabledReason;
  loading?: boolean;
};

export const FeaturesParcelFormSectionAddToWarehouse = ({
  form,
  disabledReason,
  loading,
}: Props) => {
  const { t } = useTranslation();
  const inputId = useId();
  const disabled = disabledReason !== null;

  const hint = (() => {
    if (loading) return t('parcels.form.addToWarehouse.checking');
    switch (disabledReason) {
      case 'no-trek-id':
        return t('parcels.form.addToWarehouse.disabled.emptyTrekId');
      case 'already-in-warehouse':
        return t('parcels.form.addToWarehouse.disabled.alreadyInWarehouse');
      default:
        return t('parcels.form.addToWarehouse.hint');
    }
  })();

  return (
    <Section title={t('parcels.form.sections.addToWarehouse')}>
      <Controller
        control={form.control}
        name="add_to_warehouse"
        render={({ field }) => (
          <label
            htmlFor={inputId}
            className={cn(
              'flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 transition-colors',
              disabled
                ? 'cursor-not-allowed opacity-70'
                : 'cursor-pointer hover:border-brand-300',
            )}
          >
            <input
              id={inputId}
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 disabled:cursor-not-allowed"
              checked={Boolean(field.value)}
              disabled={disabled}
              onChange={(e) => field.onChange(e.target.checked)}
              data-cy="parcel-create-add-to-warehouse"
            />
            <div className="flex flex-1 flex-col gap-1">
              <span className="text-sm font-medium text-ink">
                {t('parcels.form.addToWarehouse.label')}
              </span>
              <span className="text-xs text-ink-muted">{hint}</span>
            </div>
          </label>
        )}
      />
    </Section>
  );
};
